import { useCallback, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useApolloClient } from '@apollo/client';

import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import SimpleModal from 'lib/modal/simple-modal';
import {
  SimpleModalContentStyled,
  SimpleModalVerticalActionsStyled,
} from 'lib/modal/simple-modal.styles';
import { pushPlannerAddEvent } from 'approot/planner/planner-data-tracking';
import { COMPLETION_STATUS_COMPLETED } from 'global/constants';
import { InlineServerError } from 'approot/account/server-error';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { Heading16, PStyled } from 'approot/shared/styles/typography.style';
import { spacing4 } from 'approot/shared/styles/spacing.style';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import PillsPicker from 'approot/shared/pills/pills-picker.hooks';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import DatePickerRangeFields from 'approot/shared/date-picker/date-picker-range-fields';
import {
  toDatePickerFormat,
  toIsoDateFormat,
} from 'approot/shared/date-picker/date-picker.utils';
import {
  DATE_INPUT_TYPE,
  START_DATE_FIELD,
  END_DATE_FIELD,
  DATE_INPUT_OPTIONS,
  TODAY,
  YESTERDAY,
  CUSTOM,
  FormData,
} from './complete.constants';
import {
  UPDATE_LESSON_IN_PLANNER,
  UPDATE_ASSESSMENT_IN_PLANNER,
} from './complete.graphql';
import type {
  UpdateLessonInPlannerMutation,
  UpdateLessonInPlannerMutationVariables,
} from './complete.graphql.generated';
import type {
  UpdateAssessmentInPlannerMutation,
  UpdateAssessmentInPlannerMutationVariables,
} from './complete.graphql.generated';
import { getPlannerItems, pushCompleteEvent } from './complete.utils';
import type { CompletionFragment } from '../planner/manage/planner.graphql.generated';
import { getTermStartAndEndDates } from 'approot/shared/logic/terms';
import { OldClassInfoBanner } from 'approot/classes/classes-banners';
import { updateUnitInPlannerCache } from 'approot/unit/add-to-planner/add-to-planner.utils';

type Props = {
  contentId: number;
  unitId: number;
  isLesson: boolean;
  isInPlanner: boolean;
  closeModal: () => void;
};

const Complete = ({
  contentId,
  unitId,
  isLesson,
  isInPlanner,
  closeModal,
}: Props) => {
  const { showSuccess } = useSnackbar();
  const currentClass = useCurrentClassVar();
  const isFromCurrentTeachingYear = currentClass?.isFromCurrentTeachingYear;

  const client = useApolloClient();

  const { start, end } = useMemo(
    () => getTermStartAndEndDates(currentClass?.terms),
    [currentClass]
  );

  const { initialDate, initialDateInputType } = useMemo(() => {
    if (isFromCurrentTeachingYear) {
      return {
        initialDate: toDatePickerFormat(new Date()),
        initialDateInputType: TODAY,
      };
    } else {
      // if we are outside of the current teaching year set the
      // initial date to the last day of the last term
      return {
        initialDate: toDatePickerFormat(end),
        initialDateInputType: CUSTOM,
      };
    }
  }, [isFromCurrentTeachingYear, end]);

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [DATE_INPUT_TYPE]: initialDateInputType,
      [START_DATE_FIELD]: initialDate,
      [END_DATE_FIELD]: initialDate,
    },
  });
  const { handleSubmit, watch } = formMethods;

  const dateInputType = watch(DATE_INPUT_TYPE);

  const [
    updateLessonInPlanner,
    { loading: lessonCompletionLoading, error: lessonCompletionError },
  ] = useLoggingMutation<
    UpdateLessonInPlannerMutation,
    UpdateLessonInPlannerMutationVariables
  >(UPDATE_LESSON_IN_PLANNER);

  const [
    updateAssessmentInPlanner,
    { loading: assessmentCompletionLoading, error: assessmentCompletionError },
  ] = useLoggingMutation<
    UpdateAssessmentInPlannerMutation,
    UpdateAssessmentInPlannerMutationVariables
  >(UPDATE_ASSESSMENT_IN_PLANNER);

  const onComplete = useCallback(
    (completion: CompletionFragment) => {
      closeModal();
      showSuccess('Completion Date Updated');

      pushCompleteEvent(
        isLesson,
        contentId,
        completion.id!,
        completion.endDate!,
        completion.startDate!
      );
    },
    [contentId, isLesson, closeModal, showSuccess]
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      let start = data[START_DATE_FIELD];
      let end = data[END_DATE_FIELD];

      if (dateInputType === TODAY) {
        const today = new Date();
        start = toDatePickerFormat(today);
        end = toDatePickerFormat(today);
      } else if (dateInputType === YESTERDAY) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        start = toDatePickerFormat(yesterday);
        end = toDatePickerFormat(yesterday);
      }

      const startDateIso = toIsoDateFormat(start);
      const endDateIso = toIsoDateFormat(end);

      if (!currentClass) return;

      if (startDateIso && endDateIso && startDateIso <= endDateIso) {
        if (isLesson) {
          updateLessonInPlanner({
            variables: {
              input: {
                classId: currentClass.id,
                lessonId: contentId,
                status: COMPLETION_STATUS_COMPLETED,
                startDate: new Date(startDateIso),
                endDate: new Date(endDateIso),
                unitId,
              },
            },
          })
            .then(res => {
              const result = res.data?.updateLessonInPlanner;
              if (result?.lesson.completion) {
                if (result.unitAddedToPlanner && result.planner) {
                  updateUnitInPlannerCache(client, result.unitId!, [
                    {
                      plannerItemGroupId: result.plannerItemGroupId!,
                      plannerId: result.planner.id,
                      __typename: 'UnitPlannerInfo',
                      term: result.term!,
                      plannerItems: getPlannerItems(
                        result.lesson,
                        result.planner.id,
                        unitId
                      )!,
                    },
                  ]);
                  pushPlannerAddEvent({
                    plannerId: result.planner?.id!,
                    unitId: result.unitId!,
                    term: `${result.term!}`,
                    plannerItemGroupId: result.plannerItemGroupId!,
                    isComingSoon: false,
                    isComingSoonPreview: false,
                  });
                }
                onComplete(result.lesson.completion);
              }
            })
            .catch(() => {});
        } else {
          updateAssessmentInPlanner({
            variables: {
              input: {
                classId: currentClass.id,
                assessmentId: contentId,
                status: COMPLETION_STATUS_COMPLETED,
                startDate: new Date(startDateIso),
                endDate: new Date(endDateIso),
                unitId,
              },
            },
          })
            .then(res => {
              const result = res.data?.updateAssessmentInPlanner;
              if (result?.assessment.completion) {
                if (result.unitAddedToPlanner && result.planner) {
                  updateUnitInPlannerCache(client, result.unitId!, [
                    {
                      plannerItemGroupId: result.plannerItemGroupId!,
                      plannerId: result.planner.id,
                      __typename: 'UnitPlannerInfo',
                      term: result.term!,
                      plannerItems: getPlannerItems(
                        result.assessment,
                        result.planner.id,
                        unitId
                      )!,
                    },
                  ]);
                  pushPlannerAddEvent({
                    plannerId: result.planner?.id!,
                    unitId: result.unitId!,
                    term: `${result.term!}`,
                    plannerItemGroupId: result.plannerItemGroupId!,
                    isComingSoon: false,
                    isComingSoonPreview: false,
                  });
                }
                onComplete(result.assessment.completion);
              }
            })
            .catch(() => {});
        }
      }
    },
    [
      dateInputType,
      currentClass,
      isLesson,
      updateLessonInPlanner,
      contentId,
      unitId,
      onComplete,
      client,
      updateAssessmentInPlanner,
    ]
  );

  return (
    <SimpleModal
      isVisible
      isOverflowable
      title="Complete"
      headerTitle="Complete"
      dataTrackingLabel="content-complete"
      onClose={closeModal}
    >
      <SimpleModalContentStyled>
        {!isFromCurrentTeachingYear && (
          <OldClassInfoBanner userClass={currentClass} />
        )}
        {isInPlanner ? (
          <PStyled>
            When did you teach this {isLesson ? 'Lesson' : 'Assessment'}?
          </PStyled>
        ) : (
          <>
            <Heading16 as="h2" marginBottom={spacing4}>
              When did you teach this {isLesson ? 'Lesson' : 'Assessment'}?
            </Heading16>
            <PStyled>
              Completed {isLesson ? 'Lessons' : 'Assessments'} can be viewed
              from this class&rsquo; Planner.
            </PStyled>
          </>
        )}
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {isFromCurrentTeachingYear && (
              <PillsPicker
                hasBorder
                fieldName={DATE_INPUT_TYPE}
                legendLabel="Date Completed"
                options={DATE_INPUT_OPTIONS}
              />
            )}

            {(!isFromCurrentTeachingYear || dateInputType === CUSTOM) && (
              <DatePickerRangeFields
                isRequired
                fromFieldName={START_DATE_FIELD}
                toFieldName={END_DATE_FIELD}
                initialFromValue={toIsoDateFormat(initialDate)}
                initialToValue={toIsoDateFormat(initialDate)}
                minimumStartDate={start}
                maximumEndDate={end}
              />
            )}

            {(lessonCompletionError || assessmentCompletionError) && (
              <InlineServerError />
            )}
            <SimpleModalVerticalActionsStyled>
              <PrimaryButton
                data-selector="save-mark-complete-button"
                loading={lessonCompletionLoading || assessmentCompletionLoading}
                disabled={
                  lessonCompletionLoading || assessmentCompletionLoading
                }
              >
                Complete
              </PrimaryButton>
            </SimpleModalVerticalActionsStyled>
          </form>
        </FormProvider>
      </SimpleModalContentStyled>
    </SimpleModal>
  );
};

export default Complete;
